import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Category } from '@sbt-web/category-utils';
import { useViewport } from '@sbt-web/hooks';
import { FilterURI, type ListFilter } from '@sbt-web/network/types';
import { Combobox, FormGroup, SelectBoxSkeleton, type ComboboxOption, type ComboboxPlainOption } from '@sbt-web/ui';
import { useDataSource } from '@/client/hooks/useDataSource';
import { comboboxOptionMapper, useValueList, ValueListData, ValueListStatus } from '@/client/hooks/useValueList';
import { FilterLabel } from '../common/FilterLabel';
import type { CommonFilterProps } from '../common/types';
const filterableSelectorEnabledFilterURIs: string[] = [FilterURI.CarBrand, FilterURI.BikeBrand, FilterURI.MarketClothingBrand, FilterURI.MarketClothingSize] as const;
const getSelectedLabel = (valueList: ValueListData['value'], selectedValues: string[]): string | undefined => valueList?.values?.filter(({
  key
}) => selectedValues.includes(key)).map(({
  value
}) => value).join(',');
const shouldUseFilterableListSelector = (filterURI: string): boolean => filterableSelectorEnabledFilterURIs.includes(filterURI);
const transformListToString = (values?: string[]): string | undefined => Array.isArray(values) && values.length > 0 ? values.join(',') : undefined;
const transformStringToList = (value?: string): string[] => value?.split(',') ?? [];
type Props = CommonFilterProps & {
  filter: ListFilter;
  category?: Category;
  onSelect: (key: string, value?: string, label?: string) => void;
  initialValue?: string;
  dependenciesValues?: {
    [key: string]: string;
  };
};
const FilterableListSelector = t0 => {
  const $ = _c(45);
  const {
    category,
    dependenciesValues,
    filter,
    initialValue,
    showBadgeNew,
    onSelect
  } = t0;
  const {
    isMobile
  } = useViewport();
  const [hideMostFrequent, setHideMostFrequent] = useState(false);
  let t1;
  if ($[0] !== initialValue) {
    t1 = transformStringToList(initialValue);
    $[0] = initialValue;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [selectedValues, setSelectedValues] = useState(t1);
  const [shouldCommit, setShouldCommit] = useState(false);
  const {
    dataSource,
    dependenciesFilled
  } = useDataSource(filter.dataSource, filter.dependencies, dependenciesValues);
  const valueListData = useValueList(dataSource, category?.id, !dependenciesFilled);
  const {
    value: valueList,
    state: valueListState
  } = valueListData;
  let t2;
  if ($[2] !== valueList?.values) {
    t2 = valueList?.values?.map(comboboxOptionMapper) ?? [];
    $[2] = valueList?.values;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const plainOptions = t2;
  let t3;
  bb0: {
    if (!valueList?.topValues) {
      t3 = plainOptions;
      break bb0;
    }
    let t4;
    if ($[4] !== valueList.topValues) {
      t4 = valueList.topValues.map(comboboxOptionMapper);
      $[4] = valueList.topValues;
      $[5] = t4;
    } else {
      t4 = $[5];
    }
    let t5;
    if ($[6] !== t4) {
      t5 = {
        label: "Pi\xF9 frequenti",
        options: t4
      };
      $[6] = t4;
      $[7] = t5;
    } else {
      t5 = $[7];
    }
    let t6;
    if ($[8] !== plainOptions) {
      t6 = {
        label: "Tutti",
        options: plainOptions
      };
      $[8] = plainOptions;
      $[9] = t6;
    } else {
      t6 = $[9];
    }
    let t7;
    if ($[10] !== t5 || $[11] !== t6) {
      t7 = [t5, t6];
      $[10] = t5;
      $[11] = t6;
      $[12] = t7;
    } else {
      t7 = $[12];
    }
    t3 = t7;
  }
  const groupedOptions = t3;
  const options = hideMostFrequent ? plainOptions : groupedOptions;
  let t4;
  if ($[13] !== filter.queryString || $[14] !== onSelect || $[15] !== valueList) {
    t4 = list => {
      onSelect(filter.queryString, transformListToString(list), getSelectedLabel(valueList, list));
      setHideMostFrequent(false);
    };
    $[13] = filter.queryString;
    $[14] = onSelect;
    $[15] = valueList;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  const commitMultipleValueChange = t4;
  let t5;
  let t6;
  if ($[17] !== commitMultipleValueChange || $[18] !== selectedValues || $[19] !== shouldCommit) {
    t5 = () => {
      if (shouldCommit) {
        commitMultipleValueChange(selectedValues);
        setShouldCommit(false);
      }
    };
    t6 = [commitMultipleValueChange, selectedValues, shouldCommit];
    $[17] = commitMultipleValueChange;
    $[18] = selectedValues;
    $[19] = shouldCommit;
    $[20] = t5;
    $[21] = t6;
  } else {
    t5 = $[20];
    t6 = $[21];
  }
  useEffect(t5, t6);
  let t7;
  if ($[22] !== filter.queryString || $[23] !== onSelect || $[24] !== valueList?.values) {
    t7 = value => {
      const label = valueList?.values?.find(v => v.key === value)?.value;
      onSelect(filter.queryString, value, label);
      setHideMostFrequent(false);
    };
    $[22] = filter.queryString;
    $[23] = onSelect;
    $[24] = valueList?.values;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  const handleOnSingleValueChange = t7;
  let t8;
  if ($[26] !== commitMultipleValueChange) {
    t8 = value_0 => {
      const newSelectedValues = value_0 ?? [];
      setSelectedValues(prevSelectedValues => {
        const isRemoval = newSelectedValues.length < prevSelectedValues.length;
        if (isRemoval) {
          commitMultipleValueChange(newSelectedValues);
        }
        return newSelectedValues;
      });
    };
    $[26] = commitMultipleValueChange;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  const handleOnMultipleValueChange = t8;
  let t9;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = input => setHideMostFrequent(!!input);
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  const handleOnSearch = t9;
  if (valueListState === ValueListStatus.ERROR) {
    return;
  }
  let t10;
  if ($[29] !== filter.label || $[30] !== showBadgeNew) {
    t10 = <FilterLabel showBadgeNew={showBadgeNew}>{filter.label}</FilterLabel>;
    $[29] = filter.label;
    $[30] = showBadgeNew;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  let t11;
  if ($[32] !== filter.label || $[33] !== filter.multiSelection || $[34] !== handleOnMultipleValueChange || $[35] !== handleOnSingleValueChange || $[36] !== initialValue || $[37] !== isMobile || $[38] !== options || $[39] !== selectedValues || $[40] !== valueListState) {
    t11 = valueListState === ValueListStatus.LOADING ? <SelectBoxSkeleton expanded={filter.multiSelection} flavor={isMobile ? "large" : "small"} /> : <div>{filter.multiSelection ? <Combobox multiple={true} ariaLabel={filter.label} options={options} value={selectedValues} onValueChange={handleOnMultipleValueChange} onMenuClose={() => setShouldCommit(true)} onSearch={handleOnSearch} closeMenuOnSelect={false} size={isMobile ? "lg" : "sm"} placeholder="Qualsiasi" /> : <Combobox ariaLabel={filter.label} options={options} value={initialValue} onValueChange={handleOnSingleValueChange} onSearch={handleOnSearch} size={isMobile ? "lg" : "sm"} placeholder="Qualsiasi" />}</div>;
    $[32] = filter.label;
    $[33] = filter.multiSelection;
    $[34] = handleOnMultipleValueChange;
    $[35] = handleOnSingleValueChange;
    $[36] = initialValue;
    $[37] = isMobile;
    $[38] = options;
    $[39] = selectedValues;
    $[40] = valueListState;
    $[41] = t11;
  } else {
    t11 = $[41];
  }
  let t12;
  if ($[42] !== t10 || $[43] !== t11) {
    t12 = <FormGroup>{t10}{t11}</FormGroup>;
    $[42] = t10;
    $[43] = t11;
    $[44] = t12;
  } else {
    t12 = $[44];
  }
  return t12;
};
export { FilterableListSelector, getSelectedLabel, shouldUseFilterableListSelector, transformListToString, transformStringToList };